import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PAGE_SIZE_OPTIONS } from '../hooks/usePagination';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

const buildPageWindow = (currentPage: number, totalPages: number): number[] => {
  const windowSize = Math.min(5, totalPages);
  if (windowSize <= 0) return [];

  let start: number;
  if (totalPages <= 5) {
    start = 1;
  } else if (currentPage <= 3) {
    start = 1;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
  } else {
    start = currentPage - 2;
  }

  return Array.from({ length: windowSize }, (_, i) => start + i);
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}) => {
  if (totalItems === 0) return null;

  const firstItem = (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(currentPage * pageSize, totalItems);
  const pages = buildPageWindow(currentPage, totalPages);
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  const navButton =
    'relative inline-flex items-center px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 text-sm text-slate-400">
        <span>
          Showing <span className="font-medium text-slate-200">{firstItem}</span> to{' '}
          <span className="font-medium text-slate-200">{lastItem}</span> of{' '}
          <span className="font-medium text-slate-200">{totalItems}</span>
        </span>
        <label className="flex items-center gap-2">
          <span className="hidden sm:inline">Rows</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border-slate-600 bg-slate-700 py-1 pl-2 pr-7 text-sm text-slate-100 focus:border-indigo-500 focus:ring-indigo-500"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <nav className="inline-flex items-center overflow-hidden rounded-lg border border-slate-700" aria-label="Pagination">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={isFirst} className={`${navButton} rounded-l-lg`}>
          <ChevronLeftIcon className="h-4 w-4" />
          <span className="sr-only">Previous</span>
        </button>
        {pages.map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={`relative inline-flex items-center border-l border-slate-700 px-4 py-2 text-sm font-medium transition-colors ${
              currentPage === pageNum
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            {pageNum}
          </button>
        ))}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLast}
          className={`${navButton} rounded-r-lg border-l border-slate-700`}
        >
          <ChevronRightIcon className="h-4 w-4" />
          <span className="sr-only">Next</span>
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
